// /reset-password/:token — the link a password-reset email points at.
// Reuses pages/LoginPage.module.css, same reasoning as
// pages/ForgotPasswordPage.jsx's own header comment.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';
import { useLanguage } from '../context/LanguageContext.jsx';
import { PasswordInput } from 'shared/components/PasswordInput/index.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import PortalShell from '../components/PortalShell.jsx';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './LoginPage.module.css';

async function confirmReset({ token, newPassword }) {
  return apiClient.post('/auth/password-reset/confirm', { token, newPassword });
}

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { t, lang } = useLanguage();
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: { newPassword: '', confirmPassword: '' } });
  const newPassword = watch('newPassword');

  const confirmMutation = useMutation({ mutationFn: confirmReset });
  const { toastOk, toastErr } = useActionFeedback();

  const onSubmit = (values) => {
    confirmMutation.mutate(
      { token, newPassword: values.newPassword },
      {
        onSuccess: () => {
          setDone(true);
          toastOk(t('portal.resetPassword.doneToast'));
        },
        onError: (err) =>
          toastErr(err, ['PASSWORD_RESET_TOKEN_NOT_FOUND', 'PASSWORD_RESET_TOKEN_EXPIRED']),
      }
    );
  };

  if (done) {
    return (
      <PortalShell backTo="/login" width="narrow">
        <div className={styles.card}>
          <h1 className={styles.title}>{t('portal.resetPassword.doneTitle')}</h1>
          <p className={styles.subtitle}>{t('portal.resetPassword.doneBody')}</p>
          <Link to="/login" className={styles.linkButton}>
            {t('portal.resetPassword.goToLogin')}
          </Link>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell backTo="/login" width="narrow">
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1 className={styles.title}>{t('portal.resetPassword.title')}</h1>
        <p className={styles.subtitle}>{t('portal.resetPassword.subtitle')}</p>

        <label className={styles.field}>
          <span className={styles.label}>{t('portal.resetPassword.newPassword')}</span>
          <PasswordInput
            lang={lang}
            autoComplete="new-password"
            {...register('newPassword', { required: true, minLength: 8, maxLength: 200 })}
          />
          {errors.newPassword && (
            <span className={styles.fieldError}>
              {t('portal.registerDetails.passwordTooShort')}
            </span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t('portal.resetPassword.confirmPassword')}</span>
          <PasswordInput
            lang={lang}
            autoComplete="new-password"
            {...register('confirmPassword', {
              required: true,
              validate: (value) => value === newPassword,
            })}
          />
          {errors.confirmPassword && (
            <span className={styles.fieldError}>
              {t('portal.registerDetails.passwordMismatch')}
            </span>
          )}
        </label>

        <button type="submit" className={styles.submitButton} disabled={confirmMutation.isPending}>
          {confirmMutation.isPending && <Spinner />}{' '}
          {confirmMutation.isPending
            ? t('portal.resetPassword.submitting')
            : t('portal.resetPassword.submit')}
        </button>
      </form>
    </PortalShell>
  );
}
